/**
 * TagSelectors — reusable contact-tag UI components.
 *
 * Originally lived inline at the bottom of ClientsPanel.jsx. Extracted
 * here Feb 2026 so the Contacts page (sidebar Contacts tab) can use the
 * same UI without copy-paste drift. Both pages now share:
 *   - TagPicker         — inline multi-select pill row (used in edit dialog)
 *   - TagFilterPopover  — header filter chip with count badge + Manage button
 *   - TagManagerDialog  — full CRUD UI for the workspace tag catalog
 *   - ColorPicker       — tiny native <input type="color"> wrapper
 *
 * Backend (unchanged):
 *   GET    /booking/tags?workspace_id=…
 *   POST   /booking/tags
 *   PATCH  /booking/tags/{tag_id}
 *   DELETE /booking/tags/{tag_id}?hard=true
 *
 * NOTE: kept verbatim from the ClientsPanel original to guarantee zero
 * visual or behavioural regression in the existing Clients view.
 */
import { useCallback, useEffect, useState } from "react";
import { Tag, Plus, ChevronDown, Edit3, Trash2, X, Check, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

export function TagPicker({ catalog, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = new Set(value || []);
  // Sort catalog + selected names alphabetically (case-insensitive) so the
  // contact form, the dropdown list, and the rendered pills all match
  // the order the user sees in the Tag Manager.
  const sortedCatalog = (catalog || []).slice().sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  const sortedSelectedNames = (value || []).slice().sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
  );
  // Build the visible pill list from the workspace catalog so we always
  // render the latest color even if the contact was tagged a while ago.
  // Tags on the contact whose name is no longer in the catalog (e.g. the
  // admin deleted a tag) still render but with a neutral gray pill.
  const selectedTags = sortedSelectedNames.map((name) => {
    const cat = sortedCatalog.find((t) => t.name === name);
    return cat || { tag_id: `orphan-${name}`, name, color: "#82828c" };
  });
  const matches = sortedCatalog.filter((t) => {
    if (!query.trim()) return true;
    return t.name.toLowerCase().includes(query.trim().toLowerCase());
  });
  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange(Array.from(next));
  };
  const remove = (name) => {
    const next = new Set(selected);
    next.delete(name);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="tag-picker">
      {selectedTags.map((t) => (
        <span
          key={t.tag_id}
          className="text-[10px] font-medium uppercase tracking-wider pl-2.5 pr-1 py-1 inline-flex items-center gap-1 leading-none"
          style={{
            borderRadius: 9999,
            background: t.color,
            color: "#ffffff",
          }}
          data-testid={`tag-pill-${t.name}`}
        >
          {t.name}
          <button
            type="button"
            onClick={() => remove(t.name)}
            className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-black/20 transition-colors"
            aria-label={`Remove ${t.name} tag`}
            data-testid={`tag-pill-remove-${t.name}`}
          >
            <X className="w-2.5 h-2.5" strokeWidth={3} />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 inline-flex items-center gap-1 border border-dashed border-[#005e7a]/40 text-[#005e7a] hover:border-[#005e7a] hover:bg-[#005e7a]/5 transition-all"
            style={{ borderRadius: 9999 }}
            data-testid="tag-picker-add-button"
          >
            <Plus className="w-3 h-3" strokeWidth={2.5} />
            {selectedTags.length ? "Edit tags" : "Add tags"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2" data-testid="tag-picker-popover">
          {(catalog || []).length === 0 ? (
            <p className="text-xs text-[#82828c] text-center py-4 px-2">
              No tags in this workspace yet. Use <strong>Manage tags</strong> on the Contacts page to add some.
            </p>
          ) : (
            <>
              {(catalog || []).length > 8 && (
                <div className="relative mb-2">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#82828c]" />
                  <input
                    type="text"
                    className="w-full bg-[#f5efe6] text-xs pl-7 pr-2 py-1.5 rounded outline-none focus:bg-white focus:ring-1 focus:ring-[#005e7a]"
                    placeholder="Search tags…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="tag-picker-search"
                  />
                </div>
              )}
              <div className="max-h-64 overflow-y-auto bloq-scroll-visible" data-testid="tag-picker-list">
                {matches.length === 0 ? (
                  <p className="text-xs text-[#82828c] text-center py-3">No tags match &ldquo;{query}&rdquo;.</p>
                ) : matches.map((t) => {
                  const active = selected.has(t.name);
                  return (
                    <button
                      key={t.tag_id}
                      type="button"
                      onClick={() => toggle(t.name)}
                      className="w-full text-left flex items-center gap-2 px-2 py-1.5 hover:bg-[#f5efe6] transition-colors text-xs"
                      data-testid={`tag-picker-option-${t.tag_id}`}
                    >
                      {/* Checkbox indicator */}
                      <span
                        className="w-3.5 h-3.5 flex items-center justify-center border flex-shrink-0"
                        style={{
                          borderRadius: 3,
                          borderColor: active ? t.color : "#c4c4cb",
                          background: active ? t.color : "transparent",
                        }}
                      >
                        {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                      </span>
                      <span
                        className="font-medium uppercase tracking-wider text-[10px] px-2 py-0.5 leading-none"
                        style={{
                          borderRadius: 9999,
                          background: `${t.color}22`,
                          color: t.color,
                        }}
                      >
                        {t.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {selected.size > 0 && (
                <div className="border-t border-[#e3dccc] mt-2 pt-2 flex items-center justify-between">
                  <span className="text-[10px] text-[#82828c]">
                    {selected.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="text-[10px] text-[#82828c] hover:text-[#1a1a1f] underline"
                    data-testid="tag-picker-clear"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Tag filter — dropdown popover with count badge + Manage Tags shortcut.
export function TagFilterPopover({ catalog, value, onChange, onManage }) {
  const selected = new Set(value || []);
  const count = value.length;
  // Alphabetical (case-insensitive) — matches the Tag Manager order.
  const sortedCatalog = (catalog || []).slice().sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5"
          data-testid="tag-filter-trigger"
        >
          <Tag className="w-3.5 h-3.5" /> Filter by tag
          {count > 0 && (
            <span className="bg-[#005e7a] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">{count}</span>
          )}
          <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        {sortedCatalog.length === 0 ? (
          <p className="text-xs text-[#82828c] text-center py-3">No tags yet — create some via Manage tags.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto" data-testid="tag-filter-list">
            {sortedCatalog.map((t) => {
              const active = selected.has(t.name);
              return (
                <button
                  key={t.tag_id}
                  type="button"
                  onClick={() => {
                    const next = new Set(selected);
                    if (active) next.delete(t.name); else next.add(t.name);
                    onChange(Array.from(next));
                  }}
                  className="text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 transition-all"
                  style={{
                    borderRadius: 9999,
                    background: active ? t.color : `${t.color}22`,
                    color: active ? "#ffffff" : t.color,
                  }}
                  data-testid={`tag-filter-${t.tag_id}`}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#e3dccc]">
          <button onClick={() => onChange([])} className="text-[11px] text-[#82828c] hover:text-[#1a1a1f] underline disabled:opacity-50" disabled={count === 0} data-testid="tag-filter-clear">
            Clear filter
          </button>
          <button onClick={onManage} className="text-xs text-[#005e7a] hover:underline inline-flex items-center gap-1" data-testid="manage-tags-button">
            <Tag className="w-3 h-3" /> Manage tags
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Tag manager — CRUD UI for the workspace tag catalog. Mirrors the
// categories manager pattern: list at top, inline add row, per-row edit/
// archive/delete. Tag names cascade-rename across contacts server-side.
// ---------------------------------------------------------------------------
export function TagManagerDialog({ open, onClose, workspaceId }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#005e7a");
  const [editing, setEditing] = useState({}); // tag_id → {name, color}

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/booking/tags`, { params: { workspace_id: workspaceId } });
      const sorted = (r.data || []).slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
      );
      setTags(sorted);
    } catch { setTags([]); }
    finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addTag = async () => {
    if (!newName.trim()) return;
    try {
      await api.post(`/booking/tags`, { workspace_id: workspaceId, name: newName.trim(), color: newColor });
      setNewName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create tag"); }
  };

  const saveEdit = async (tagId) => {
    const edit = editing[tagId];
    if (!edit) return;
    try {
      await api.patch(`/booking/tags/${tagId}`, { name: edit.name, color: edit.color });
      setEditing((prev) => { const { [tagId]: _, ...rest } = prev; return rest; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const deleteTag = async (tagId, name) => {
    if (!window.confirm(`Delete tag "${name}"? It will be removed from all contacts.`)) return;
    try {
      await api.delete(`/booking/tags/${tagId}?hard=true`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="tag-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-[#005e7a]" /> Manage tags</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[#82828c] -mt-1 mb-2">
          Tags help group contacts (VIP, monthly, referred-by-X). Renaming a tag updates every contact using it.
        </p>

        {/* Existing tags */}
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1 bloq-scroll-visible">
          {loading ? (
            <p className="text-sm text-[#82828c] text-center py-4">Loading…</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-[#82828c] text-center py-4">No tags yet — add your first one below.</p>
          ) : tags.map((t) => {
            const e = editing[t.tag_id];
            return (
              <div key={t.tag_id} className="flex items-center gap-2 py-1.5" data-testid={`tag-row-${t.tag_id}`}>
                {e ? (
                  <>
                    <input
                      className="bloq-input h-8 flex-1 text-sm"
                      value={e.name}
                      onChange={(ev) => setEditing((prev) => ({ ...prev, [t.tag_id]: { ...e, name: ev.target.value } }))}
                    />
                    <ColorPicker value={e.color} onChange={(c) => setEditing((prev) => ({ ...prev, [t.tag_id]: { ...e, color: c } }))} />
                    <button onClick={() => saveEdit(t.tag_id)} className="text-xs text-[#005e7a] font-medium" data-testid={`tag-save-${t.tag_id}`}>Save</button>
                    <button onClick={() => setEditing((prev) => { const { [t.tag_id]: _, ...rest } = prev; return rest; })} className="text-xs text-[#82828c]">Cancel</button>
                  </>
                ) : (
                  <>
                    <span
                      className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 flex-1 leading-none"
                      style={{ borderRadius: 9999, background: `${t.color}22`, color: t.color }}
                    >
                      {t.name}
                    </span>
                    <button onClick={() => setEditing((prev) => ({ ...prev, [t.tag_id]: { name: t.name, color: t.color } }))} className="text-[#82828c] hover:text-[#005e7a] p-1" data-testid={`tag-edit-${t.tag_id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteTag(t.tag_id, t.name)} className="text-[#82828c] hover:text-[#b91c1c] p-1" data-testid={`tag-delete-${t.tag_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Add new tag */}
        <div className="border-t border-[#e3dccc] pt-3 mt-3">
          <p className="text-[10px] uppercase tracking-wider text-[#82828c] font-semibold mb-2">Add new tag</p>
          <div className="flex items-center gap-2">
            <input
              className="bloq-input flex-1"
              placeholder="VIP, monthly, referred-by-X…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addTag(); }}
              data-testid="new-tag-name"
            />
            <ColorPicker value={newColor} onChange={setNewColor} />
            <button onClick={addTag} className="bloq-btn-primary text-sm" data-testid="add-tag-button"><Plus className="w-4 h-4" /></button>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <button onClick={onClose} className="bloq-btn-secondary text-sm" data-testid="tag-manager-close">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ColorPicker({ value, onChange }) {
  return (
    <input
      type="color"
      className="bloq-input h-10 w-12 cursor-pointer p-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Pick a color"
    />
  );
}

// ---------------------------------------------------------------------------
// BulkTagDialog — applied tag changes across many contacts in one shot.
// Used by the bulk-action toolbar on both Contacts.jsx and ClientsPanel.jsx.
//
// Mode semantics (mirrors the backend):
//   add     — append the chosen tags to whatever each contact already has
//   remove  — strip the chosen tags from each contact (others untouched)
//   replace — overwrite every selected contact's tags with the chosen list
//
// Calls POST /api/booking/clients/bulk/tags directly — endpoint works for
// both clients and plain contacts (same Mongo collection).
// ---------------------------------------------------------------------------
export function BulkTagDialog({ open, onClose, workspaceId, contactIds, catalog, onApplied }) {
  const [mode, setMode] = useState("add"); // add | remove | replace
  const [picked, setPicked] = useState([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state every time the dialog opens so we don't carry over a
  // stale selection from a previous bulk action.
  useEffect(() => {
    if (open) { setMode("add"); setPicked([]); setQuery(""); }
  }, [open]);

  const sorted = (catalog || []).slice().sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  const matches = sorted.filter((t) =>
    !query.trim() || t.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  const toggle = (name) => {
    setPicked((prev) => prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]);
  };

  const apply = async () => {
    if (!contactIds?.length) return;
    if (mode !== "replace" && picked.length === 0) {
      toast.error("Pick at least one tag");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/booking/clients/bulk/tags`, {
        contact_ids: contactIds,
        mode,
        tags: picked,
      }, { params: { workspace_id: workspaceId } });
      const n = r.data?.affected || 0;
      const verb = mode === "add" ? "tagged" : mode === "remove" ? "untagged" : "updated";
      toast.success(`${verb} ${n} contact${n === 1 ? "" : "s"}`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply tags");
    } finally {
      setBusy(false);
    }
  };

  const modeCopy = {
    add: "Add these tags to every selected contact. Existing tags stay put.",
    remove: "Remove these tags from every selected contact. Other tags are preserved.",
    replace: "Overwrite each selected contact's tags with this list. Choosing nothing clears tags.",
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-tag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-[#005e7a]" />
            Bulk edit tags · {contactIds?.length || 0} selected
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-[#f5efe6]" style={{ borderRadius: 9999 }} data-testid="bulk-tag-mode">
          {[
            { k: "add",     label: "Add" },
            { k: "remove",  label: "Remove" },
            { k: "replace", label: "Replace" },
          ].map((m) => (
            <button
              key={m.k}
              onClick={() => setMode(m.k)}
              className={`text-xs font-medium uppercase tracking-wider py-1.5 transition-colors ${
                mode === m.k ? "bg-[#005e7a] text-white" : "text-[#4a4a52] hover:bg-white"
              }`}
              style={{ borderRadius: 9999 }}
              data-testid={`bulk-tag-mode-${m.k}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#82828c] -mt-1">{modeCopy[mode]}</p>

        {/* Catalog list */}
        {sorted.length === 0 ? (
          <p className="text-sm text-[#82828c] text-center py-6">
            No tags in this workspace yet. Use <strong>Manage tags</strong> to add some first.
          </p>
        ) : (
          <>
            {sorted.length > 8 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#82828c]" />
                <input
                  type="text"
                  className="w-full bg-[#f5efe6] text-xs pl-8 pr-2 py-2 outline-none focus:bg-white focus:ring-1 focus:ring-[#005e7a]"
                  style={{ borderRadius: 6 }}
                  placeholder="Search tags…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="bulk-tag-search"
                />
              </div>
            )}
            <div className="max-h-64 overflow-y-auto -mx-1 px-1 bloq-scroll-visible" data-testid="bulk-tag-list">
              {matches.length === 0 ? (
                <p className="text-xs text-[#82828c] text-center py-3">No tags match &ldquo;{query}&rdquo;.</p>
              ) : matches.map((t) => {
                const active = picked.includes(t.name);
                return (
                  <button
                    key={t.tag_id}
                    type="button"
                    onClick={() => toggle(t.name)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 hover:bg-[#f5efe6] transition-colors text-xs"
                    data-testid={`bulk-tag-option-${t.tag_id}`}
                  >
                    <span
                      className="w-3.5 h-3.5 flex items-center justify-center border flex-shrink-0"
                      style={{
                        borderRadius: 3,
                        borderColor: active ? t.color : "#c4c4cb",
                        background: active ? t.color : "transparent",
                      }}
                    >
                      {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                    </span>
                    <span
                      className="font-medium uppercase tracking-wider text-[10px] px-2 py-0.5"
                      style={{ borderRadius: 9999, background: `${t.color}22`, color: t.color }}
                    >
                      {t.name}
                    </span>
                  </button>
                );
              })}
            </div>
            {picked.length > 0 && (
              <div className="border-t border-[#e3dccc] pt-2 flex items-center justify-between">
                <span className="text-[10px] text-[#82828c]">{picked.length} selected</span>
                <button
                  type="button"
                  onClick={() => setPicked([])}
                  className="text-[10px] text-[#82828c] hover:text-[#1a1a1f] underline"
                  data-testid="bulk-tag-clear"
                >
                  Clear
                </button>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary text-sm" data-testid="bulk-tag-cancel">Cancel</button>
          <button
            onClick={apply}
            disabled={busy || sorted.length === 0}
            className="bloq-btn-primary text-sm disabled:opacity-60"
            data-testid="bulk-tag-apply"
          >
            {busy ? "Applying…" : `Apply (${mode})`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
